const toString = Object.prototype.toString

/**
 * 判断v是否定义（非undefined, null）
 * @param v 任意值
 * @returns true or false
 */
export function isDef<T = unknown>(v: T): v is T {
  return v !== undefined && v !== null
}

/**
 * 判断v是否未定义（undefined, null）
 * @param v 任意值
 * @returns true or false
 */
export function isUnDef(v: unknown): v is undefined | null {
  return v === undefined || v === null
}

/**
 * 判断v是不是对象
 * @param v 任意值
 * @returns true or false
 */
export function isObject(v: unknown) {
  return Object(v) === v
}

/**
 * 判断v是不是原生对象
 * @param v 任意值
 * @returns true or false
 */
export function isPlainObject(v: unknown): v is object {
  return is(v, 'Object')
}

/**
 * 判断v是不是指定的类型，使用Object.prototype.toString
 * @param v 任意值
 * @returns true or false
 */
export function is(v: unknown, t: string) {
  return toString.call(v) === `[object ${t}]`
}

/**
 * 判断v是不是日期对象，使用Object.prototype.toString
 * @param v 任意值
 * @returns true or false
 */
export function isDate(v: unknown): v is Date {
  return is(v, 'Date')
}

/**
 * 判断v是不是正则表达式对象，使用Object.prototype.toString
 * @param v 任意值
 * @returns true or false
 */
export function isRegExp(v: unknown): v is RegExp {
  return is(v, 'RegExp')
}

/**
 * 判断v是不是数字，
 * @param v 任意值
 * @returns true or false
 */
export function isNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v)
}

/**
 * 判断v是不是字符串，
 * @param v 任意值
 * @returns true or false
 */
export function isString(v: unknown): v is string {
  return typeof v === 'string'
}

/**
 * 判断v是不是函数，
 * @param v 任意值
 * @returns true or false
 */
export function isFunction(v: unknown): v is Function {
  return typeof v === 'function'
}

/**
 * 判断a, b是不是深度相等，递归遍历所有属性
 * @param a 任意值
 * @param b 任意值
 * @returns true or false
 */
export function isDeepEqual(a: any, b: any) {
  if (a === b) {
    return true
  }
  if (a !== null && b !== null && typeof a === 'object' && typeof b === 'object') {
    const aCtor = a.constructor
    const bCtor = b.constructor

    if (aCtor !== bCtor) {
      return false
    }

    if (aCtor === Array) {
      const aLen = a.length
      if (aLen !== b.length) {
        return false
      }
      for (let i = 0; i < aLen; i++) {
        if (isDeepEqual(a[i], b[i]) !== true) {
          return false
        }
      }
      return true
    }

    if (aCtor === Map) {
      const aSize = a.size
      if (aSize !== b.size) {
        return false
      }
      for (const [k] of a) {
        if (b.has(k) !== true) {
          return false
        }
      }
      for (const [k, v] of a) {
        if (isDeepEqual(v, b.get(k)) !== true) {
          return false
        }
      }
      return true
    }

    if (aCtor === Set) {
      const aSize = a.size
      if (aSize !== b.size) {
        return false
      }
      const i = a.entries()
      for (const [k] of i) {
        if (b.has(k) !== true) {
          return false
        }
      }
      return true
    }

    if (a.buffer !== undefined && a.buffer !== null && a.buffer.constructor === ArrayBuffer) {
      const aLen = a.length
      if (aLen !== b.length) {
        return false
      }
      for (let i = 0; i < aLen; i++) {
        if (a[i] !== b[i]) {
          return false
        }
      }
      return true
    }

    if (aCtor === RegExp) {
      return a.source === b.source && a.flags === b.flags
    }

    if (a.valueOf !== Object.prototype.valueOf) {
      return a.valueOf() === b.valueOf()
    }

    if (a.toString !== Object.prototype.toString) {
      return a.toString() === b.toString()
    }

    const aKeys = Object.keys(a)
    if (aKeys.length !== Object.keys(b).length) {
      return false
    }

    for (const k of aKeys) {
      if (isDeepEqual(a[k], b[k]) !== true) {
        return false
      }
    }

    return true
  }

  return a !== a && b !== b
}

/**
 * 判断v是不是数组，
 * @param v 任意值
 * @returns true or false
 */
export function isArray(v: unknown): v is Array<any> {
  return Array.isArray(v)
}

export function isUrl(path: string): boolean {
  const reg =
    /(((^https?:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+(?::\d+)?|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)$/
  return reg.test(path)
}
